package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.SourceTranslationRequest
import dev.lingvo.shared.TranslationState
import dev.lingvo.site.api.ApiClient
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

/** Guarded keys writes cannot downgrade to the legacy route on an old server or a source conflict. */
internal object KeysCaptionPort : CaptionKeysPort {
    override suspend fun write(snapshot: CaptionSnapshot, value: String): CaptionSnapshot {
        check(snapshot.canWrite) { "This caption is read-only" } // i18n-ignore: internal contract diagnostic
        val source = requireNotNull(snapshot.sourceView) { "Reload the caption to establish its source revision" } // i18n-ignore: internal validation diagnostic
        val result = ApiClient.updateTranslationForSource(snapshot.project, snapshot.keyId, snapshot.locale,
            SourceTranslationRequest(source, snapshot.targetRevision, value = value, state = TranslationState.TRANSLATED))
        // A canonical-source write creates a new source view. Keep its identity for a guarded Undo.
        val basis = if (snapshot.locale == snapshot.sourceLocale) {
            try { ApiClient.sourceChange(snapshot.project, snapshot.keyId, snapshot.locale).currentSource?.view }
            catch (failure: Exception) { null }
        } else source
        return snapshot.copy(value = result.value.orEmpty(), draft = result.value.orEmpty(), state = result.state,
            targetRevision = result.revision, sourceView = basis, error = null)
    }
}

/** The action retains the saved key/locale identity even after the editor switches language. */
internal fun showCaptionSavedToast(
    toasts: dev.lingvo.site.ds.ToastState,
    scope: kotlinx.coroutines.CoroutineScope,
    receipt: CaptionReceipt,
    restored: (CaptionSnapshot) -> Unit,
    failed: (String) -> Unit,
) {
    toasts.showWithAction(
        trf("studio.saved_caption", "Saved to {language} · {key}",
            "language" to receipt.saved.language, "key" to receipt.saved.key),
        tr("studio.editor.caption_binding_api.undo", "Undo"),
        onAction = {
            scope.launch {
                try { restored(receipt.undo()) } catch (failure: Exception) {
                    failed(failure.message ?: tr("studio.editor.caption_binding_api.couldnt_undo_this_caption_reload_it", "Couldn't undo this caption. Reload it and try again."))
                }
            }
        },
    )
}

/** Keys metadata supplies the consequence count and exact guarded identities before editing starts. */
internal suspend fun loadCaptionSnapshot(
    project: String,
    keyId: String,
    locale: String,
    language: String,
    layer: dev.lingvo.shared.screenshots.doc.TextLayer,
    font: dev.lingvo.shared.screenshots.text.FontStack,
    canWriteTarget: Boolean,
    canWriteSource: Boolean,
): CaptionSnapshot {
    val detail = ApiClient.keyDetail(project, keyId)
    val change = ApiClient.sourceChange(project, keyId, locale)
    require(change.keyId == keyId && change.locale == locale && !detail.key.isPlural) { "The caption identity does not match its key" } // i18n-ignore: internal contract diagnostic
    val target = change.target
    return CaptionSnapshot(project, keyId, detail.key.name, locale, language, detail.sourceLocale,
        target?.value.orEmpty(), state = target?.state ?: TranslationState.EMPTY,
        sourceView = change.currentSource?.view, targetRevision = target?.revision,
        canWrite = (if (locale == detail.sourceLocale) canWriteSource else canWriteTarget) && (target == null || target.revision != null),
        affectedTranslations = detail.translations.count { it.locale != detail.sourceLocale && !it.value.isNullOrEmpty() },
        layer = layer, font = font, sourceText = change.currentSource?.value)
}
